// Type-only: GridRowDescriptor is a pure function type (no ag-grid types in
// its signature), so this keeps the package free of any ag-grid dependency.
import type { GridRowDescriptor } from "@appretio/ui-kit";
import { FeedbackCellContext, FeedbackPrefill } from "./FeedbackContext";

/**
 * Generic "Report this value" capture for AG Grid cells — the grid-agnostic
 * core every data grid shares. Typed structurally (no ag-grid dependency in
 * this package): AG Grid's own params/api satisfy these slices, and tests
 * can exercise the mapping with plain object stubs.
 */

/** The column surface the capture reads (AG Grid `Column` satisfies it). */
export interface GridReportColumn {
  getColId: () => string;
  getColDef: () => { headerName?: string };
}

/** A displayed row (AG Grid `IRowNode` satisfies it). */
export interface GridReportRowNode {
  data?: unknown;
}

/** One selected range (AG Grid `CellRange` satisfies it). */
export interface GridReportCellRange {
  startRow?: { rowIndex: number } | null;
  endRow?: { rowIndex: number } | null;
  columns?: readonly GridReportColumn[];
}

/**
 * The GridApi slice the capture reads — every method optional/defensive.
 * Method syntax (not arrow properties) so AG Grid's own `GridApi` stays
 * structurally assignable under strict function-parameter variance.
 */
export interface GridReportApi {
  getEditingCells?(): readonly {
    rowIndex: number | null;
    column?: { getColId: () => string } | null;
  }[];
  getCellRanges?(): readonly GridReportCellRange[] | null;
  getDisplayedRowAtIndex?(index: number): GridReportRowNode | undefined;
  // Loosely typed on purpose: AG Grid's GridApi.getCellValue is an overload
  // set (incl. a `useFormatter: true` variant) that a precise structural
  // signature rejects; the capture only calls the plain (rowNode, colKey)
  // form and treats the result as unknown anyway.
  getCellValue?(params: { rowNode: unknown; colKey: unknown }): unknown;
}

/** The context-menu params slice the capture reads. */
export interface GridCellReportParams {
  column?: GridReportColumn | null;
  node?: GridReportRowNode | null;
  value?: unknown;
  api?: GridReportApi;
}

/** Range capture cap — enough to reproduce, bounded enough to store. */
export const MAX_RANGE_CELLS = 50;

/**
 * The domain extractor's identity keys, or {} when it throws: `describeRow`
 * is grid-supplied code running against arbitrary row data — a bug there
 * must degrade the report to "no identity keys", never kill the context
 * menu (or the report) it runs inside.
 */
function safeDescribeRow(
  describeRow: GridRowDescriptor | undefined,
  data: Record<string, unknown>
): Record<string, unknown> {
  if (!describeRow) return {};
  try {
    return describeRow(data);
  } catch {
    return {};
  }
}

/**
 * Map a clicked cell to the feedback prefill: category preset to
 * `data_wrong`, `context.cell` carrying the column id, the displayed value,
 * a compact scalar row snapshot, the grid's open editors, any multi-cell
 * range selection, and whatever identity `describeRow` extracts — exactly
 * the facts a data-trust triage needs (see DAS-6559).
 */
export function buildGridCellFeedbackPrefill(
  params: GridCellReportParams,
  describeRow?: GridRowDescriptor
): FeedbackPrefill | null {
  const column = params.column;
  if (!column) return null;
  const data = (params.node?.data ?? {}) as Record<string, unknown>;

  // Open editors at right-click time — the value the user is disputing may
  // sit under an uncommitted edit. (Submit-time edit state is the page
  // context's job; this is the state the user was looking at.)
  const editingCells = listGridEditingCells(params.api);
  const range = collectRangeCells(params.api, describeRow);

  const cell: FeedbackCellContext = {
    field: column.getColId(),
    header: column.getColDef().headerName ?? column.getColId(),
    value: sanitizeCellValue(params.value),
    ...safeDescribeRow(describeRow, data),
    row: compactRowSnapshot(data),
    ...(editingCells.length > 0 ? { editing_cells: editingCells } : {}),
    // A range selection wider than the clicked cell rides alongside it —
    // `cell` stays the clicked cell so the payload shape is stable.
    ...(range ?? {})
  };
  return { category: "data_wrong", cell };
}

/**
 * The grid's context-menu items with "Report this value" appended after the
 * given defaults. Generic over the item type so AG Grid's own menu-item
 * union flows through unchanged; cells without a column (empty space) keep
 * the defaults only.
 */
export function buildGridFeedbackMenuItems<TItem>(
  params: GridCellReportParams & { defaultItems?: readonly TItem[] },
  openFeedback: (prefill: FeedbackPrefill) => void,
  describeRow?: GridRowDescriptor
): (TItem | "separator" | { name: string; action: () => void })[] {
  const defaults = params.defaultItems ?? [];
  const prefill = buildGridCellFeedbackPrefill(params, describeRow);
  if (!prefill) return [...defaults];
  return [
    ...defaults,
    "separator",
    {
      name: "Report this value",
      action: () => openFeedback(prefill)
    }
  ];
}

/** Cells currently in edit mode (open editors), or [] without a grid api. */
export function listGridEditingCells(
  api: GridReportApi | undefined
): { row_index: number | null; col_id: string }[] {
  if (!api || typeof api.getEditingCells !== "function") return [];
  return api.getEditingCells().map((cell) => ({
    row_index: cell.rowIndex,
    // `column` is typed optional on AG Grid's CellPosition; an editor
    // without one has nothing addressable to report.
    col_id: cell.column?.getColId() ?? "unknown"
  }));
}

/**
 * Capture the current range selection when it spans more than one cell:
 * every selected cell's field/header/value plus the row identity, capped at
 * MAX_RANGE_CELLS entries with `cells_total` carrying the real count.
 * Returns null for empty/single-cell selections — the clicked `cell`
 * already covers those.
 *
 * The total comes from the range BOUNDS (rows × columns per range), never
 * from scanning: a select-all on a big server-side grid must cost at most
 * MAX_RANGE_CELLS row lookups, not one per selected row.
 */
function collectRangeCells(
  api: GridReportApi | undefined,
  describeRow?: GridRowDescriptor
): Pick<FeedbackCellContext, "cells" | "cells_total"> | null {
  if (!api || typeof api.getCellRanges !== "function") return null;
  const ranges = api.getCellRanges() ?? [];
  const entries: NonNullable<FeedbackCellContext["cells"]> = [];
  let total = 0;
  for (const range of ranges) {
    const start = range.startRow?.rowIndex;
    const end = range.endRow?.rowIndex;
    if (start == null || end == null) continue;
    const [first, last] = start <= end ? [start, end] : [end, start];
    const columns = range.columns ?? [];
    total += (last - first + 1) * columns.length;
    for (
      let rowIndex = first;
      rowIndex <= last && entries.length < MAX_RANGE_CELLS;
      rowIndex++
    ) {
      const rowNode = api.getDisplayedRowAtIndex?.(rowIndex);
      const data = (rowNode?.data ?? {}) as Record<string, unknown>;
      for (const column of columns) {
        if (entries.length >= MAX_RANGE_CELLS) break;
        const colId = column.getColId();
        entries.push({
          row_index: rowIndex,
          field: colId,
          header: column.getColDef().headerName ?? colId,
          value: sanitizeCellValue(readCellValue(api, rowNode, colId, data)),
          ...safeDescribeRow(describeRow, data)
        });
      }
    }
  }
  if (total <= 1) return null;
  return { cells: entries, cells_total: total };
}

/** The cell's value via the grid's value service, raw row data as fallback. */
function readCellValue(
  api: GridReportApi,
  rowNode: GridReportRowNode | undefined,
  colId: string,
  data: Record<string, unknown>
): unknown {
  if (rowNode && typeof api.getCellValue === "function") {
    try {
      return api.getCellValue({ rowNode, colKey: colId });
    } catch {
      // Fall through to the raw field — a report must never throw.
    }
  }
  return asScalar(data[colId]);
}

/** Keep only the row's scalar fields — renderers/objects add noise. */
function compactRowSnapshot(
  data: Record<string, unknown>
): Record<string, unknown> {
  const snapshot: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (asScalar(value) !== undefined || value === null) {
      snapshot[key] = value;
    }
  }
  return snapshot;
}

/**
 * Make a captured cell value safe to JSON-serialize: scalars (and null /
 * undefined) pass through untouched, everything else — objects a valueGetter
 * returned, BigInt, functions, circular structures — degrades to String(),
 * the same discipline compactRowSnapshot applies to row fields. A report
 * must survive JSON.stringify both in the transparency pane and in the POST
 * body.
 */
function sanitizeCellValue(value: unknown): unknown {
  if (value == null) return value;
  if (asScalar(value) !== undefined) return value;
  try {
    return String(value);
  } catch {
    // e.g. an object whose toString throws — the field name still reports.
    return "<unserializable value>";
  }
}

/** The value when it's a reportable scalar, else undefined. */
function asScalar(value: unknown): string | number | boolean | undefined {
  const type = typeof value;
  if (type === "string" || type === "number" || type === "boolean") {
    return value as string | number | boolean;
  }
  return undefined;
}
